import React, { useState, useRef, useEffect, useCallback } from "react";
import { Box, Snackbar, Button, Grid } from "@mui/material";
import ReactQuill from "react-quill";
import { Trans } from "react-i18next";
import MUITextEditorToolbar from "./TextEditorToolBar";
import "react-quill/dist/quill.snow.css";
import { GREEN_MAIN, GREEN_LOGO, WHITE } from "../../theme/colors";
import { t } from "i18next";
import { useUserContext } from "../../hooks/auth/UserProvider";
import settings from "../../utilities/settings";

export const EditableTextArea: React.FC = () => {
  const [referral, setReferral] = React.useState<string | null>(null);
  const [htmlContent, setHtmlContent] = useState("");
  const [range, setRange] = useState<any>();
  const [copiedText, setCopiedText] = useState<string | null>(null);
  const [openError, setOpenError] = useState(false);
  const transContentRef = useRef<HTMLDivElement>(null);
  const quillRef = useRef<ReactQuill>(null);
  const {user} = useUserContext();

  useEffect(() => {
    if (user?.refCode) {
      const url = new URL(settings.ReferralBaseUrl);
      const cleanedPathname = url.pathname.endsWith('/') ? url.pathname.slice(0, -1) : url.pathname;
      url.pathname = `${cleanedPathname}/${user.refCode}`;
      setReferral(url.toString());
    }
  }, [user]);

  useEffect(() => {
    if (transContentRef.current && referral) {
      const content = transContentRef.current.innerHTML;
      setHtmlContent(content);
    }
  }, [referral]);

  const handleFormat = (format: string, value: any = true) => {
    const quill = quillRef.current?.getEditor();
    if (quill) {
      quill.format(format, value);
    }
  };

  const insertLink = () => {
    const quill = quillRef.current?.getEditor();
    if (quill && range) {
      const currentFormat = quill.getFormat(range);
      if (currentFormat.link) {
        quill.format("link", false); // rimuove il link
      } else if (range.length > 0) {
        quill.format("link", referral);
      } else {
        setOpenError(true);
      }
    } else {
      setOpenError(true);
    }
  };

  useEffect(() => {
    const quill = quillRef.current?.getEditor();
    if (!quill) return;

    const handleSelectionChange = (range: any) => {
      setRange(range);
    };

    quill.on("selection-change", handleSelectionChange);
    return () => {
      quill.off("selection-change", handleSelectionChange);
    };
  }, []);

  const copyToClipboard = () => {
    const tempElement = document.createElement("div");
    tempElement.innerHTML = htmlContent;
    const plainText = tempElement.innerText;
  
    document.body.appendChild(tempElement);
  
    const selection = window.getSelection();
    const range = document.createRange();
    range.selectNodeContents(tempElement);
    selection?.removeAllRanges();
    selection?.addRange(range);
    document.execCommand("copy");
    selection?.removeAllRanges();
    document.body.removeChild(tempElement);
  
    setCopiedText(plainText.slice(0, 200)); // mostra primi 200 caratteri
  };

  useEffect(() => {
    const container = document.querySelector(".ql-editor");
    if (!container) return;
  
    const handleLinkClick = (e: Event) => {
      const target = e.target as HTMLElement;
      if (target.tagName === "A") {
        e.preventDefault();
        const href = (target as HTMLAnchorElement).href;
        if (href) window.open(href, "_blank");
      }
    };
  
    container.addEventListener("click", handleLinkClick);
    return () => {
      container.removeEventListener("click", handleLinkClick);
    };
  }, []);
  

  return (
    <Box sx={{ fontFamily: "poppins", color: GREEN_MAIN }}>
      <div
        ref={transContentRef}
        style={{ position: "absolute", left: "-9999px", visibility: "hidden" }}
      >
        <Trans
          i18nKey="present-circuit-mail"
          components={[
            <a href={referral!} target="_blank" rel="noopener noreferrer" />,
            <br />
          ]}
        />
      </div>

      <MUITextEditorToolbar onFormat={handleFormat} onInsertLink={insertLink} />

      <ReactQuill
        ref={quillRef}
        value={htmlContent}
        onChange={setHtmlContent}
        modules={{ toolbar: false }}
        formats={["bold", "italic", "size", "link"]}
        theme="snow"
        preserveWhitespace
        style={{ fontFamily: "inherit", minHeight: 150 }}
      />

      <Grid container xs={12} justifyContent='center' paddingTop={5}>
        <Grid item xs={8} sm={3}>
          <Button
            variant="contained"
            onClick={copyToClipboard}
            sx={{
                bgcolor: GREEN_LOGO,
                "&:hover": { bgcolor: "darkgreen" },
                color:WHITE,
                border: "1px solid " + GREEN_LOGO,
                fontSize:'1.2rem'
            }}
          >
            {t("COPIA")}
          </Button>
        </Grid>
      </Grid>

      <Snackbar
        open={openError || !!copiedText}
        autoHideDuration={3000}
        onClose={() => {
          setOpenError(false);
          setCopiedText(null);
        }}
        message={
          openError
            ? t("Devi selezionare un testo a cui inserire il link")
            : t("Testo copiato") // anteprima del testo
        }
        sx={{
          "& .MuiSnackbarContent-root": {
            backgroundColor: GREEN_LOGO,
            border: `1px solid ${WHITE}`,
            color: WHITE
          }
        }}
      />

      <style>
        {`
          .ql-editor a {
            pointer-events: auto;
            cursor: pointer;
            color: #0077cc;
            text-decoration: underline;
          }
        `}
      </style>
    </Box>
  );
};
